<template lang="pug">
  management-page.scrollable(title="Summary"
                :creatable="false"
                :searchable="false"
                :showHeader="false")
    template(v-slot:body)
      aiq-row(:gutter="16" justify="end")
          .time-range-filter
            aiq-date-picker(v-model="dateRange"
                          @change="handleDatePickerChange($event)"
                          type="daterange"
                          placeholder="Select date range",
                          popper-class="metrics-time-range__date-picker-popup"
                          :shortcuts="datePickerOptions.shortcuts"
                          :disabled-date="datePickerOptions.disabledDate"
                          :teleported="false"
                          format="YYYY/MM/DD"
                          :clearable="false"
                          :default-time="getDatePickerDefaultTime()")
      include ./selectableFilters/SelectableFilters.pug
      .chart-grid
        aiq-row(:gutter="16")
          aiq-col(:span="24")
            overview-chart(v-if="timezone"
                          :dateRange="dateRangeUtc"
                          :chartConfig="messagesByChannelsConfig"
                          :filters="selectedFilters"
                          grouped=true
                          visualization='aiq-bar-chart'
                          :downloadWithTimeTicks="true"
                          :reportFileName="returnReportFileName('Messages By Channel')"
                          :style="{ height: '100%' }"
                          :useDebounceForLoadMetrics="true"
                          deriveLinesFromResponse
                          derivedLineType='bar'
                          :getOriginalData="getOriginalData"
                          @onChartDataChange="onChartDataChange($event)"
                          :formatXTick="xLabelsMsgByChannelsFormat"
                          @onXLabelsChange="xLabelsMessagesByChannel=$event"
                          :shouldHandlePartialDates="true"
                          )

</template>

<script>
import
{ mapGetters } from 'vuex';
import ManagementPage from '@/components/ManagementPage/ManagementPage.vue';
import OverviewChart from '@/components/Chart.vue';
import { createPickerDateRangeOption, datePickerDefaultOptions, lastAvailableMonthAndQuarterRange, dstDateRangeUtc } from '@/pages/Metrics/configs/dateRange';
import _, { cloneDeep } from 'lodash';
import messagesByChannelsConfig from '../../configs/messagesByChannels';
import activePlatformCustomersChartConfig from '../../configs/activePlatformCustomersChartConfig';
import PlatformFiltersMixin from '@/pages/Metrics/Platform/children/selectableFilters/SelectableFilters';
import dayjs from '@/localPlugins/dayjs';
import debounce from 'lodash/debounce';
const xAxisFormatForActiveCustomers = (value) => dayjs(value).format('MMM DD');
import { useTimeZone } from '@/libs';
const LOAD_METRICS_DEBOUNCE_TIME = 100;


export default {
  pageResource: '/metrics/platform',
  name: 'Messages',
  mixins: [PlatformFiltersMixin],
  components: {
    ManagementPage,
    OverviewChart,
  },
  data() {
    return {
      timezone: null,
      messagesByChannelsConfig,
      chartData: [],
      xLabelsMessagesByChannel: {},
      activePlatformCustomersChartConfig,
      xAxisFormatForActiveCustomers,
      getOriginalData: () => this.originalData,
    };
  },
  computed: {
    ...mapGetters({
      configs: 'configs/graphsConfigs',
    }),
    datePickerOptions() {
      return {
        ...datePickerDefaultOptions(),
        shortcuts: lastAvailableMonthAndQuarterRange.map(createPickerDateRangeOption),
      };
    },
    dateRangeUtc() {
      return dstDateRangeUtc(this.dateRange);
    },
  },
  async mounted() {
    this.timezone = await useTimeZone(
      Intl.DateTimeFormat().resolvedOptions().timeZone,
      this.$store.dispatch,
      this.$store._state.data.configs,
    );
    this.debouncedLoadMetric = _.debounce(this.loadMetric, LOAD_METRICS_DEBOUNCE_TIME);
  },
  methods: {
    loadMetric() {
      return Promise.resolve();
    },
    xLabelsMsgByChannelsFormat(date) {
      const label = this.xLabelsMessagesByChannel[dayjs(date).valueOf()];
      const partialIndicator = this.isDataPartial(date) ? ' *' : '';
      return `${label || dayjs(date).format('MMM DD')}${partialIndicator}`;
    },
    returnReportFileName(chartName = 'data') {
      const [startTimestamp, endTimestamp] = this.dateRange;
      const startDate = dayjs.isDayjs(startTimestamp)
        ? startTimestamp.format('YYYY-MM-DD')
        : dayjs(startTimestamp).format('YYYY-MM-DD');
      const endDate = dayjs.isDayjs(endTimestamp)
        ? endTimestamp.format('YYYY-MM-DD')
        : dayjs(endTimestamp).format('YYYY-MM-DD');
      return `${chartName.replaceAll(' ', '_')}_${startDate}_${endDate}`;
    },

    onChartDataChange(data) {
      this.chartData = data?.dataset?.[0]?.data_points || [];
      this.originalData = cloneDeep(data);
    },
    isDataPartial(date) {
      const timeStamp = new Date(date).getTime();
      const isPartial = this.chartData.find(item => item.x === timeStamp)?.isPartial;
      return isPartial;
    },
  },
  watch: {
    dateRange: debounce(function onDateChange(newValue, oldValue = []) {
      const [start, end] = oldValue;
      if (!(start && end)) {
        return;
      }
      // Reload filter options for already loaded filters when date range changes
      for (const endpoint in this.loadedFilterEndpoints) {
        this.loadFilterOption(endpoint);
      }
    }, 1),
  },
};
</script>

<style lang="scss" scoped>
@import "../../../../styles/aiq-variables.scss";
@import "./selectableFilters/SelectableFilters.scss";

.scrollable {
  overflow-y: auto;
}

.el-card:not(:first-of-type) {
  margin-top: 24px;
}

.time-range-filter {
  position: relative;
  margin-bottom: 24px;
}

:deep(.c3-circles-total_convos) {
opacity: 0 !important;
}

.chart-configuration {
  display: flex;
  margin-bottom: 16px;
  justify-content: space-between;
  .breakdown {
    display: flex;
    align-items: center;
    flex-grow: 1;
  }
  .members-filter {
    display: flex;
    align-items: center;
  }

  .export-command {
    display: flex;
    height: 30px;
    width: 40px;
    justify-content: center;
    align-items: center;
    margin-left: 16px;
    padding: 0;
  }
}
</style>
